// Slapend zetten, wakker maken en archiveren — T2.5.
//
// Een slapende Kavel is géén gearchiveerde Kavel. Archief betekent "uit de
// portefeuille"; slapend betekent "hier gebeurt nu niets". Het verschil is
// niet cosmetisch: gearchiveerd telt nergens meer mee en staat op een eigen
// scherm, slapend blijft bij zijn fase horen en is met één klik terug.
//
// Vier dingen die deze hook bewaakt en die de losse `.update()`-aanroepen op
// de Charging-schermen geen van alle deden:
//
//   1. **De geschreven rijen worden geteld.** `.select('id')` achter elke
//      mutatie en `assertRowsWritten` erachter. Een door RLS geblokkeerde
//      UPDATE geeft geen error maar nul rijen; zonder deze telling zou het
//      scherm "Kavel slaapt" melden terwijl er niets gebeurd is.
//
//   2. **Archiveren maakt eerst wakker.** Migratie 084 zet
//      `num_nonnulls(deleted_at, dormant_at) <= 1` op de tabel: de drie
//      standen sluiten elkaar uit. Wie een slapende Kavel archiveert zonder
//      `dormant_at` te wissen, krijgt 23514. Daarom doet `archiveer` beide in
//      één statement.
//
//   3. **Archiveren blijft werken zónder 084.** Dit is de reparatie na de
//      scepticus-ronde, en de reden is hard: vóór T2.5 wérkte archiveren, en
//      een tussenstand waarin het stuk is omdat de migratie nog niet is
//      goedgekeurd, is onaanvaardbaar. `dormant_at` onvoorwaardelijk meesturen
//      laat PostgREST het HELE verzoek weigeren met PGRST204 — dus ook het
//      `deleted_at` dat er los van staat. Daarom peilt `dormantKolomBestaat()`
//      de kolom één keer per sessie (zelfde aanpak als `epseAvailable` in
//      `useScenarioEnergyLink` voor migratie 076), en laat `archiveerWaarden`
//      het veld weg wanneer de kolom er niet is. Archiveren doet dan precies
//      wat het vóór T2.5 deed.
//
//      Slapend zetten en wakker maken doen het omgekeerde: die gáán over die
//      kolom, dus zonder 084 falen ze meteen en luid, met een zin die de
//      oorzaak noemt in plaats van een kale kolomnaam.
//
//   4. **Een mislukte schrijfactie wordt niet meegeteld.** `schrijfKavelsBulk`
//      gebruikt `Promise.allSettled` en telt wat er écht is gelukt. De vijf
//      bulk-acties op de Charging-schermen meldden vóór T2.5 altijd
//      `selectedLocationIds.size`, ook als de database er geen enkele had
//      aangeraakt.

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { assertRowsWritten } from '@/lib/assertRowsWritten';

/**
 * Elke lijst die Kavels toont. Na een standwijziging moeten ze allemaal
 * opnieuw laden, anders klopt de telling op het ene scherm niet meer met die
 * op het andere.
 *
 * De laatste drie kwamen erbij na de scepticus-ronde: `ChargingExport`,
 * `ChargingPresentatie` en `ExportTableModal` bleven met verouderde data
 * staan na een slaap-actie.
 */
export const KAVEL_QUERY_KEYS: readonly string[] = [
  'charging-locations',
  'charging-acquisitie-locations',
  'charging-realisatie-locations',
  'charging-operatie-locations',
  'charging-archived-locations',
  'charging-location',
  'gauw-tabel-data',
  'charging-all-locations',
  'charging-locations-presentation',
  'export-table-data',
];

/** PostgREST-code voor "die kolom ken ik niet" bij een schrijfactie. */
const KOLOM_ONBEKEND_SCHRIJVEN = 'PGRST204';
/** Postgres-code voor "die kolom ken ik niet" bij een leesactie. */
const KOLOM_ONBEKEND_LEZEN = '42703';
/** Postgres-code voor een geschonden CHECK: hier "deze Kavel heeft twee standen". */
const STAND_CONFLICT = '23514';

const MIGRATIE_ONTBREEKT =
  'de kolom dormant_at bestaat nog niet — migratie 084 is nog niet toegepast';

/**
 * Vertaalt een PostgREST-fout naar iets dat de oorzaak noemt.
 *
 * Het onderwerp komt mee sinds de scepticus-ronde: zonder dat stond er boven
 * een mislukte archiveeractie "Archiveren mislukt — Slapend zetten kan pas
 * daarna", en dat is twee keer misleidend.
 */
export function vertaalFout(
  error: { code?: string; message?: string } | null,
  onderwerp: string,
): Error | null {
  if (!error) return null;
  if (error.code === KOLOM_ONBEKEND_SCHRIJVEN || /dormant_at/.test(error.message ?? '')) {
    return new Error(`${onderwerp} kan nog niet: ${MIGRATIE_ONTBREEKT}.`);
  }
  // De CHECK uit 084: deze Kavel draagt zowel een archiefdatum als een
  // slaapdatum. Dat kan alleen als dit scherm de kolom nog niet kende toen het
  // opende. De rauwe melding ("violates check constraint …") zegt de gebruiker
  // niets; deze wel, en de peiling is intussen weggegooid.
  if (error.code === STAND_CONFLICT) {
    return new Error(
      `${onderwerp} kan niet: deze Kavel slaapt nog. Herlaad de pagina en probeer het opnieuw.`,
    );
  }
  return new Error(error.message || 'Onbekende fout');
}

/* ── Bestaat de kolom? ─────────────────────────────────────────────────── */

/**
 * Eén peiling per sessie. `null` = nog niet gepeild.
 *
 * Module-scope en niet React-state: de uitkomst is een eigenschap van de
 * database, niet van een component, en elk scherm moet hetzelfde antwoord
 * krijgen. Exporteerbaar gereset voor de test.
 */
let dormantKolomGepeild: boolean | null = null;

/** Alleen voor tests: gooi de peiling weg. */
export function resetDormantPeiling(): void {
  dormantKolomGepeild = null;
}

/**
 * Bestaat `charging_locations.dormant_at`?
 *
 * Een `select` op een onbekende kolom geeft 42703 en raakt niets aan; dat is
 * de goedkoopste en veiligste peiling. Faalt de peiling om een andere reden
 * (netwerk, RLS), dan luidt het antwoord `true`: dan draaien we door op het
 * normale pad en komt een echte fout gewoon uit de schrijfactie. Anders zou
 * een haperend netwerk de `dormant_at`-wissing stilletjes overslaan en een
 * slapende Kavel met twee standen achterlaten.
 */
export async function dormantKolomBestaat(client: typeof supabase): Promise<boolean> {
  if (dormantKolomGepeild !== null) return dormantKolomGepeild;

  const { error } = await client.from('charging_locations').select('dormant_at').limit(1);
  const ontbreekt =
    !!error &&
    ((error as any).code === KOLOM_ONBEKEND_LEZEN ||
      /dormant_at/.test((error as any).message ?? ''));

  // Alleen een CONCLUSIEF antwoord onthouden (scepticus-ronde 2). Een peiling
  // die om een andere reden faalt — netwerk, 500 — zei eerder óók "de kolom
  // bestaat", en dat bleef de hele sessie staan: gemeten liep één hapering uit
  // op archiveren dat daarna bij elke poging faalde tot de gebruiker herlaadde.
  // Nu geldt `true` alleen voor dit ene verzoek en peilt de volgende opnieuw.
  const conclusief = !error || ontbreekt;
  if (conclusief) dormantKolomGepeild = !ontbreekt;
  return !ontbreekt;
}

/* ── De standwijzigingen als kolomwaarden ──────────────────────────────── */

/**
 * Geëxporteerd en niet inline, omdat dit de plek is waar "de drie standen
 * sluiten elkaar uit" waar of onwaar wordt: `archiveerWaarden` moet
 * `dormant_at` op null zetten, en `slaapWaarden`/`wakkerWaarden` moeten
 * elkaars inverse zijn. Alle drie zijn te toetsen zonder een browser, een
 * React-tree of een database.
 */
export const slaapWaarden = (nu: Date = new Date()) => ({ dormant_at: nu.toISOString() });
export const wakkerWaarden = () => ({ dormant_at: null });

/**
 * Archiveren. `dormantBeschikbaar` bepaalt of de wissing meegaat.
 *
 * Zonder 084 bestaat de kolom niet en is er ook niets te wissen: het
 * resultaat is exact de UPDATE die hier vóór T2.5 stond. Mét 084 gaat de
 * wissing mee, want anders botst een slapende Kavel op de CHECK (23514).
 */
export const archiveerWaarden = (
  nu: Date = new Date(),
  dormantBeschikbaar = true,
): Record<string, string | null> =>
  dormantBeschikbaar
    ? { deleted_at: nu.toISOString(), dormant_at: null }
    : { deleted_at: nu.toISOString() };

/* ── Schrijven, en tellen wat er geschreven is ─────────────────────────── */

/**
 * Schrijf één wijziging weg en tel de geraakte rijen.
 *
 * De client komt als argument binnen zodat deze functie zonder Supabase te
 * toetsen is — het is de enige plek waar de telling gebeurt, en juist die
 * moet bewijsbaar zijn.
 */
export async function schrijfKavelStand(
  client: typeof supabase,
  kavelId: string,
  waarden: Record<string, string | null>,
  onderwerp: string,
  nu: Date = new Date(),
): Promise<void> {
  const { data, error } = await client
    .from('charging_locations')
    // `as any`: dormant_at staat niet in src/integrations/supabase/types.ts en
    // dat bestand wordt niet geregenereerd (V1-hash-lock).
    .update({ ...waarden, updated_at: nu.toISOString() } as any)
    .eq('id', kavelId)
    .select('id');

  // De peiling is per sessie gecachet, en de database kan onder een openstaand
  // scherm veranderen: wordt 084 toegepast terwijl de tab openstaat, dan denkt
  // deze sessie nog dat de kolom ontbreekt (en omgekeerd na een reverse). Beide
  // fouten zeggen precies dát — PGRST204 "kolom ken ik niet", 23514 "deze Kavel
  // heeft twee standen, dus de wissing is overgeslagen" — dus gooien we de
  // peiling weg. De volgende poging peilt opnieuw en gaat goed, zonder F5.
  const code = (error as any)?.code;
  if (code === KOLOM_ONBEKEND_SCHRIJVEN || code === STAND_CONFLICT) resetDormantPeiling();

  const vertaald = vertaalFout(error as any, onderwerp);
  if (vertaald) throw vertaald;

  // Dit is de kern: geen error én nul rijen betekent geblokkeerd, niet gelukt.
  assertRowsWritten(data, onderwerp);
}

export interface BulkUitkomst {
  gelukt: string[];
  mislukt: { id: string; fout: string }[];
}

/**
 * Dezelfde schrijfactie op meerdere Kavels, met een eerlijke telling.
 *
 * `Promise.allSettled` en niet `Promise.all`: bij `all` verdwijnt na de eerste
 * afwijzing de uitslag van de rest, en dat is precies waarom de vijf
 * bulk-acties op de Charging-schermen altijd het volle aantal meldden.
 */
export async function schrijfKavelsBulk(
  client: typeof supabase,
  kavelIds: readonly string[],
  waarden: Record<string, string | null>,
  onderwerp: string,
  nu: Date = new Date(),
): Promise<BulkUitkomst> {
  const uitkomsten = await Promise.allSettled(
    kavelIds.map((id) => schrijfKavelStand(client, id, waarden, onderwerp, nu)),
  );

  const gelukt: string[] = [];
  const mislukt: { id: string; fout: string }[] = [];
  uitkomsten.forEach((uitkomst, i) => {
    if (uitkomst.status === 'fulfilled') gelukt.push(kavelIds[i]);
    else mislukt.push({ id: kavelIds[i], fout: (uitkomst.reason as Error)?.message ?? 'onbekend' });
  });

  return { gelukt, mislukt };
}

/**
 * Eén melding voor een bulk-actie, en hij is waar in alle drie de gevallen.
 *
 * Pure functie, want dit is de zin waarin de leugen zat: "12 locaties
 * gearchiveerd" terwijl er nul waren gewijzigd. `variant` stuurt de toast.
 */
export function bulkMelding(
  uitkomst: BulkUitkomst,
  werkwoord: string,
): { title: string; description?: string; variant?: 'destructive' } {
  const gelukt = uitkomst.gelukt.length;
  const totaal = gelukt + uitkomst.mislukt.length;

  if (totaal === 0) return { title: 'Niets geselecteerd' };

  if (gelukt === 0) {
    return {
      title: `Geen enkele Kavel ${werkwoord}`,
      description: uitkomst.mislukt[0]?.fout,
      variant: 'destructive',
    };
  }

  if (gelukt < totaal) {
    return {
      title: `${gelukt} van ${totaal} ${werkwoord}`,
      description: `${totaal - gelukt} niet gewijzigd — ${uitkomst.mislukt[0]?.fout}`,
      variant: 'destructive',
    };
  }

  return { title: `${gelukt} ${gelukt === 1 ? 'Kavel' : 'Kavels'} ${werkwoord}` };
}

/* ── De hook ───────────────────────────────────────────────────────────── */

export function useKavelSlaap() {
  const queryClient = useQueryClient();

  const ververs = useCallback(() => {
    for (const key of KAVEL_QUERY_KEYS) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  }, [queryClient]);

  /** Slapend zetten en wakker maken kunnen niet zonder de kolom. */
  const eisDormantKolom = async (onderwerp: string) => {
    if (!(await dormantKolomBestaat(supabase))) {
      throw new Error(`${onderwerp} kan nog niet: ${MIGRATIE_ONTBREEKT}.`);
    }
  };

  const slaapIn = useMutation({
    mutationFn: async (kavelId: string) => {
      await eisDormantKolom('Slapend zetten');
      await schrijfKavelStand(supabase, kavelId, slaapWaarden(), 'Slapend zetten');
    },
    onSuccess: () => {
      ververs();
      toast({
        title: 'Kavel slaapt',
        description: 'Niet gearchiveerd — hij blijft in zijn fase staan en is met één klik terug.',
      });
    },
    onError: (error: Error) =>
      toast({ title: 'Slapend zetten mislukt', description: error.message, variant: 'destructive' }),
  });

  const maakWakker = useMutation({
    mutationFn: async (kavelId: string) => {
      await eisDormantKolom('Wakker maken');
      await schrijfKavelStand(supabase, kavelId, wakkerWaarden(), 'Wakker maken');
    },
    onSuccess: () => {
      ververs();
      toast({ title: 'Kavel is weer actief' });
    },
    onError: (error: Error) =>
      toast({ title: 'Wakker maken mislukt', description: error.message, variant: 'destructive' }),
  });

  /**
   * Archiveren. Werkt mét en zónder 084 — zie punt 3 in de kop. Gebruik dit
   * overal waar een kale `update({ deleted_at })` stond.
   */
  const archiveer = useMutation({
    mutationFn: async (kavelId: string) => {
      const beschikbaar = await dormantKolomBestaat(supabase);
      await schrijfKavelStand(
        supabase,
        kavelId,
        archiveerWaarden(new Date(), beschikbaar),
        'Archiveren',
      );
    },
    onSuccess: () => {
      ververs();
      toast({ title: 'Kavel gearchiveerd', description: 'Terug te vinden op Archief.' });
    },
    onError: (error: Error) =>
      toast({ title: 'Archiveren mislukt', description: error.message, variant: 'destructive' }),
  });

  /**
   * Meerdere Kavels archiveren, met één melding die klopt. Geeft de uitkomst
   * terug zodat het scherm de selectie kan opruimen.
   */
  const archiveerVeel = useCallback(
    async (kavelIds: readonly string[]): Promise<BulkUitkomst> => {
      const beschikbaar = await dormantKolomBestaat(supabase);
      const uitkomst = await schrijfKavelsBulk(
        supabase,
        kavelIds,
        archiveerWaarden(new Date(), beschikbaar),
        'Archiveren',
      );
      ververs();
      toast(bulkMelding(uitkomst, 'gearchiveerd'));
      return uitkomst;
    },
    [ververs],
  );

  /**
   * Een willekeurig veld op meerdere Kavels zetten (stadium, eigenaar), met
   * dezelfde telling en dezelfde eerlijke melding.
   */
  const wijzigVeel = useCallback(
    async (
      kavelIds: readonly string[],
      waarden: Record<string, string | null>,
      werkwoord: string,
    ): Promise<BulkUitkomst> => {
      const uitkomst = await schrijfKavelsBulk(supabase, kavelIds, waarden, 'Bijwerken');
      ververs();
      toast(bulkMelding(uitkomst, werkwoord));
      return uitkomst;
    },
    [ververs],
  );

  /** Eén veld op één Kavel, met telling. Voor de losse stadiumwissel. */
  const wijzig = useCallback(
    async (
      kavelId: string,
      waarden: Record<string, string | null>,
      onderwerp: string,
    ): Promise<void> => {
      await schrijfKavelStand(supabase, kavelId, waarden, onderwerp);
      ververs();
    },
    [ververs],
  );

  return {
    slaapIn: slaapIn.mutate,
    slaapInAsync: slaapIn.mutateAsync,
    maakWakker: maakWakker.mutate,
    maakWakkerAsync: maakWakker.mutateAsync,
    archiveer: archiveer.mutate,
    archiveerAsync: archiveer.mutateAsync,
    archiveerVeel,
    wijzigVeel,
    wijzig,
    ververs,
    bezig: slaapIn.isPending || maakWakker.isPending || archiveer.isPending,
  };
}
